import {Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';

/**
* AI:
* Status: 'in progress'
* Type: Service
* SubType: WidgetConfig
* Reason: Configured data provider for widget data fetching
*/

import {WidgetData} from 'features/core/shared/widget/widget-data';
import {WidgetFactory} from 'features/core/shared/widget/service/widget.factory';
import {
    CommissionDecisionSnapshotDto
} from '../../../model/dto/entity/commission-decision-snapshot/commission-decision-snapshot.dto';
import {
    CommissionDecisionSnapshotService
} from '../../../service/api/commission-decision-snapshot/commission-decision-snapshot.service';

/**
* Decision-snapshot viewer + export (FR-13).
*
* Shows an immutable, audit-proof CnCommissionDecisionSnapshot:
*  - aggregate NPV (active / best scenario), horizon, discount rate,
*  - version-pin / version-hash of the involved model/rule set,
*  - approvedBy / approvedAt (NFR-Audit).
*
* The snapshot detail is rendered through mvs-widget, whose built-in toolbar provides
* the framework export mechanism (CSV / Excel). This keeps export consistent with the
* rest of the platform and avoids any FE-side recomputation (NFR-CleanCore).
*/
@Component({
    selector: 'mvs-cn-decision-snapshot',
    templateUrl: './cn-decision-snapshot.component.html',
    styleUrls: ['./cn-decision-snapshot.component.scss'],
    standalone: false
})
export class CnDecisionSnapshotComponent implements OnInit, OnChanges, OnDestroy {

    /** Snapshot id persisted by the backend when persistDecisionSnapshot=true. */
    @Input() snapshotId: number | null;

    snapshot: CommissionDecisionSnapshotDto | null;
    snapshotWidget: WidgetData;
    loading: boolean = false;
    errorMessage: string | null = null;
    initialized: boolean = false;

    constructor(private snapshotService: CommissionDecisionSnapshotService) {
    }

    ngOnInit(): void {
        this.initComponent();
    }

    /**
    * Initialize the viewer: load the snapshot and build the export widget once.
    */
    initComponent(): void {
        this.refreshComponent();
        this.initialized = true;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.initialized) {
            return;
        }
        if (changes['snapshotId']) {
            this.refreshComponent();
        }
    }

    ngOnDestroy(): void {
    }

    /**
    * AI: Load the snapshot detail and build the export-enabled widget.
    * @widget cn.decision.snapshot.detail.widget (entity, list)
    */
    refreshComponent(): void {
        this.snapshot = null;
        this.snapshotWidget = null;
        this.errorMessage = null;

        if (!this.snapshotId) {
            return;
        }

        this.loading = true;
        this.snapshotService.get(this.snapshotId).subscribe({
            next: (dto: CommissionDecisionSnapshotDto) => {
                this.snapshot = dto;
                this.loading = false;
            },
            error: () => {
                this.errorMessage = 'Der Entscheidungs-Snapshot konnte nicht geladen werden.';
                this.loading = false;
            }
        });

        // Entity-data widget exposes the framework export toolbar (CSV / Excel).
        this.snapshotWidget = WidgetFactory.createWidgetEntityData(
            'cn.decision.snapshot.detail.widget',
            'Entscheidungs-Snapshot',
            'cn.CommissionDecisionSnapshot',
            this.snapshotId
        );
    }

    /** True when a snapshot is available to display. */
    hasSnapshot(): boolean {
        return !!this.snapshot;
    }
}
